import { useEffect, useMemo, useState } from 'react';
import { enrichHttpRequestForMatchReplaceMatching } from '../utils/match-replace-preview';
import { resolveRequestForMatchReplace } from '../utils/match-replace-history-resolution';
import { useRequestBodies } from './useRequestBodies';
import {
  useRecentHistoryRequests,
  type UseRecentHistoryRequestsArgs,
} from './useRecentHistoryRequests';

export function useResolvedRecentHistoryRequests({
  targetId,
  targetType,
  limit = 50,
}: UseRecentHistoryRequestsArgs) {
  const requests = useRecentHistoryRequests({ targetId, targetType, limit });
  const { fetchBodies, fetchLargeBody, fetchLargeBodyAsBinary } =
    useRequestBodies();
  const [resolvedRequests, setResolvedRequests] = useState(() =>
    requests.map((request) =>
      enrichHttpRequestForMatchReplaceMatching(request),
    ),
  );

  useEffect(() => {
    const syncResolved = requests.map((request) =>
      enrichHttpRequestForMatchReplaceMatching(request),
    );
    setResolvedRequests(syncResolved);

    let cancelled = false;

    void (async () => {
      try {
        const nextRequests = await Promise.all(
          requests.map((request) =>
            resolveRequestForMatchReplace({
              request,
              fetchBodies,
              fetchLargeBody,
              fetchLargeBodyAsBinary,
            }),
          ),
        );

        if (!cancelled) {
          setResolvedRequests(nextRequests);
        }
      } catch (error: unknown) {
        console.warn(
          'Failed to resolve match replace history bodies',
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requests, fetchBodies, fetchLargeBody, fetchLargeBodyAsBinary]);

  return useMemo(() => resolvedRequests, [resolvedRequests]);
}
